import type { WeatherCondition, WeatherForecast, WeatherKind, WeatherWarning } from '@casa/driver-sdk';
import type { ForecastHorizon, ForecastThreshold, TileOf } from '@casa/model';
import { formatNumber, localDate } from '@casa/util';
import { drawText, layoutText, type Rect, type TextStyle } from '../../elements/flex_label.ts';
import { drawIcon, type IconDefinition } from '../../elements/icon.ts';
import type { TileFont } from '../value/value_tile.ts';

/**
 * The `forecast` tile (docs/specs/weather.md §4, issue #19): one horizon of a weather source's
 * forecast, the next hours, the next days and nights, or the week, as a row of slots that wraps
 * onto more rows where the tile is narrow (one horizon per tile: the owner, on #19). Each slot says
 * when, shows the weather's icon (two with an arrow where it changes), the temperature, rain and
 * wind. Warnings make a strip at the top, and a mark on the slots they cover.
 */

export type ForecastTile = TileOf<'forecast'>;

type Context = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

const HOUR = 3_600_000;
/** A slot's least width and height (WT-7): fewer slots rather than squeezed ones. */
export const MIN_SLOT_WIDTH = 64;
export const MIN_SLOT_HEIGHT = 100;
/** Width over height of a slot at its best. */
const SLOT_ASPECT = 0.72;

export const DIM_COLOR = '#9a9a9a';
export const WARNING_COLOR = '#f59e0b';
const WARNING_TEXT = '#1a1a1a';

/** Icons in the colours of their weather, so that a glance tells sun from rain. */
const KIND_COLORS: Readonly<Record<WeatherKind, string>> = {
    'clear': '#ffd43b',
    'few-clouds': '#ffe08a',
    'partly-cloudy': '#e3e6ea',
    'cloudy': '#d0d4da',
    'overcast': '#b8bec6',
    'fog': '#b8bec6',
    'haze': '#d9c9a3',
    'drizzle': '#8cc8ff',
    'showers': '#74b9ff',
    'rain': '#5cb3ff',
    'heavy-rain': '#3d8bfd',
    'freezing-rain': '#9fd3ff',
    'sleet': '#c5e3ff',
    'snow-showers': '#e6f4ff',
    'snow': '#f2f9ff',
    'thunderstorm': '#ffcd39',
    'squall': '#d0d4da',
    'tornado': '#ff8787',
};
/** The moon is not yellow. */
const iconColor = (condition: WeatherCondition) : string => (/moon/.test(condition.icon) && condition.kind === 'clear' ? '#c9d6ff' : KIND_COLORS[condition.kind]);

// ---- What each slot says ----------------------------------------------------------------------------

export interface ForecastSlot {
    /** "14:00", "Today", "Sat night", "Sat 25". */
    when: string;
    conditions: readonly WeatherCondition[];
    /** "12°", "14° / 8°". */
    temperature: string;
    /** "2.5 mm"; "–" where unknown; none when the tile leaves rain out. */
    rain: string | undefined;
    /** "60%", beside the rain. */
    chance: string | undefined;
    /** Little or no rain: drawn dim. */
    isDry: boolean;
    /** The colour of the rain above a threshold of the tile's (WT-9); none below them. */
    rainColor: string | undefined;
    /** "35", or "12–35" for a slot of several hours (WT-4); with the unit in `windUnit`. */
    wind: string | undefined;
    /** The colour of the wind above a threshold of the tile's (WT-9), by the strongest shown. */
    windColor: string | undefined;
    /** Where the wind blows to, in degrees (the arrow); none where unknown. */
    windToward: number | undefined;
    isWarned: boolean;
}

export interface ForecastContent {
    horizon: ForecastHorizon;
    slots: ForecastSlot[];
    warnings: readonly WeatherWarning[];
    windUnit: string;
    timeZone: string;
}

const formatterFor = (() => {
    const cache = new Map<string, Intl.DateTimeFormat>();

    return (timeZone: string, options: Intl.DateTimeFormatOptions) : Intl.DateTimeFormat => {
        const key = `${timeZone}|${JSON.stringify(options)}`;
        let formatter = cache.get(key);

        if (!formatter) {
            // The app is in English (WT-6): the names of days are English, times are 24-hour.
            formatter = new Intl.DateTimeFormat('en-GB', { timeZone, ...options });
            cache.set(key, formatter);
        }

        return formatter;
    };
})();

export const clockOf = (time: number, timeZone: string) : string => formatterFor(timeZone, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(time);
const weekdayOf = (time: number, timeZone: string) : string => formatterFor(timeZone, { weekday: 'short' }).format(time);
const dayOfMonth = (time: number, timeZone: string) : string => formatterFor(timeZone, { day: 'numeric' }).format(time);

/** A period's name (WT-4): "Today", "Tonight", "Sat", "Sat night", counted from `now` at the place. */
export function periodLabel(start: number, part: 'day' | 'night', now: number, timeZone: string) : string {
    const isToday = localDate(start, timeZone) === localDate(now, timeZone);
    // A night that began yesterday evening is still "Tonight" until 06:00.
    const isTonight = part === 'night' && (isToday || start <= now);

    if (part === 'day') {
        return isToday ? 'Today' : weekdayOf(start, timeZone);
    }

    return isTonight ? 'Tonight' : `${weekdayOf(start, timeZone)} night`;
}

const degrees = (value: number) : string => `${Math.round(value)}°`;

function rainOf(rain: number | null, chance: number, units: WeatherForecast['units'], locale: string | undefined) : { rain: string; chance: string | undefined; isDry: boolean } {
    if (rain === null) {
        return { rain: '–', chance: `${Math.round(chance * 100)}%`, isDry: chance < 0.2 };
    }

    const shown = units.rain === 'in' ? formatNumber(rain, '0.##', locale) : formatNumber(rain, '0.#', locale);

    return { rain: `${shown} ${units.rain}`, chance: chance >= 0.05 ? `${Math.round(chance * 100)}%` : undefined, isDry: rain < (units.rain === 'in' ? 0.01 : 0.2) && chance < 0.3 };
}

/**
 * The wind of a slot (WT-4, the owner on #20): a range from the calmest hour to the strongest, the
 * gusts as the high where the forecast gives them, for a slot worked out from several hours (`lowest`);
 * one value for one reading. Whole numbers.
 */
export function windRange(wind: number, lowest: number | undefined, gust: number | undefined) : { text: string; strongest: number } {
    const high = Math.round(lowest === undefined ? wind : Math.max(wind, gust ?? wind));
    const low = lowest === undefined ? high : Math.round(lowest);

    return { text: low < high ? `${low}–${high}` : String(high), strongest: high };
}

/** The colour of the highest threshold `value` is above (WT-9); none where it is above none. */
export function colorAbove(thresholds: readonly ForecastThreshold[], value: number) : string | undefined {
    let found: ForecastThreshold | undefined;

    for (const threshold of thresholds) {
        if (value > threshold.above && (!found || threshold.above > found.above)) {
            found = threshold;
        }
    }

    return found?.color;
}

/** The wind's unit as the tile shows it: "kph", shorter than "km/h" (the owner, #20). */
const windUnitOf = (unit: WeatherForecast['units']['wind']) : string => (unit === 'km/h' ? 'kph' : unit);

/** How many slots a horizon shows when the tile says none (WT-2). */
export const DEFAULT_SLOTS: Readonly<Record<ForecastHorizon, number>> = { short: 6, medium: 6, long: 7 };

/** The slots of the tile's horizon (WT-2, WT-4), as of `now`. */
export function forecastContent(tile: ForecastTile, forecast: WeatherForecast, now: number, locale?: string) : ForecastContent {
    const { horizon, showRain, showWind, showWarnings, rainColors, windColors } = tile.options;
    const count = tile.options.slots ?? DEFAULT_SLOTS[horizon];
    const { timeZone } = forecast.location;
    const units = forecast.units;
    
    type Wind = { wind: number; windLowest?: number | undefined; gust?: number | undefined; windDirection?: number | undefined };

    const slot = (when: string, conditions: WeatherCondition[], temperature: string, rain: number | null, chance: number, wind: Wind, warnings: readonly string[]) : ForecastSlot => {
        const rained = rainOf(rain, chance, units, locale);
        const blown = windRange(wind.wind, wind.windLowest, wind.gust);
        return {
            when,
            conditions,
            temperature,
            rain: showRain ? rained.rain : undefined,
            chance: showRain ? rained.chance : undefined,
            isDry: rained.isDry,
            rainColor: rain === null ? undefined : colorAbove(rainColors, rain),
            wind: showWind ? blown.text : undefined,
            windColor: colorAbove(windColors, blown.strongest),
            windToward: showWind && wind.windDirection !== undefined ? (wind.windDirection + 180) % 360 : undefined,
            isWarned: showWarnings && warnings.length > 0,
        };
    };

    const slots = horizon === 'short'
        ? forecast.hourly.filter((hour) => hour.time + HOUR > now).slice(0, count).map((hour) => slot(clockOf(hour.time, timeZone), [hour.condition], degrees(hour.temperature), hour.rain, hour.chance, hour, hour.warnings))
        : horizon === 'medium'
          ? forecast.periods.filter((period) => period.end > now).slice(0, count).map((period) =>
              // A day's high, a night's low: "Today" and "Tonight" say which (the owner, 2026-10-28: no "max" / "min").
              slot(periodLabel(period.start, period.part, now, timeZone), [period.condition, ...(period.becomes ? [period.becomes] : [])], degrees(period.temperature), period.rain, period.chance, period, period.warnings),
          )
          : forecast.daily.filter((day) => localDate(day.time + 12 * HOUR, timeZone) >= localDate(now, timeZone)).slice(0, count).map((day) => {
              const noon = day.time + 12 * HOUR;
              const when = localDate(noon, timeZone) === localDate(now, timeZone) ? 'Today' : `${weekdayOf(noon, timeZone)} ${dayOfMonth(noon, timeZone)}`;
              return slot(when, [day.condition], `${degrees(day.max)} / ${degrees(day.min)}`, day.rain, day.chance, day, day.warnings);
          });

    return {
        horizon,
        slots,
        warnings: showWarnings ? forecast.warnings.filter((warning) => warning.end > now) : [],
        windUnit: windUnitOf(units.wind),
        timeZone,
    };
}

/** The strip's line (WT-5): "Wind until 01:00", "Rain from 14:00 · +1". */
export function warningLine(warnings: readonly WeatherWarning[], now: number, timeZone: string) : string {
    const firstWarning = warnings[0];

    if (!firstWarning) {
        return '';
    }

    const when = firstWarning.start > now ? `from ${spanEdge(firstWarning.start, now, timeZone)}` : `until ${spanEdge(firstWarning.end, now, timeZone)}`;

    return `${firstWarning.title} ${when}${warnings.length > 1 ? ` · +${warnings.length - 1}` : ''}`;
}

/** A warning's edge: its time today, else the day and time. */
export const spanEdge = (time: number, now: number, timeZone: string) : string => (localDate(time, timeZone) === localDate(now, timeZone) ? clockOf(time, timeZone) : `${weekdayOf(time, timeZone)} ${clockOf(time, timeZone)}`);

// ---- Layout (WT-3, WT-7) ---------------------------------------------------------------------------------

export interface ForecastLayout {
    strip: Rect | undefined;
    /** One per slot shown: the first ones, where not all fit (WT-7). */
    slots: Rect[];
}

/** The rows and columns for `count` slots in `width` × `height`: the largest slots, at least the least size. */
export function slotGrid(count: number, width: number, height: number) : { rows: number; columns: number; shown: number } {
    const fitColumns = Math.max(1, Math.floor(width / MIN_SLOT_WIDTH));
    const fitRows = Math.max(1, Math.floor(height / MIN_SLOT_HEIGHT));
    const shown = Math.min(count, fitColumns * fitRows);
    let best = { rows: 1, columns: shown, scale: 0 };

    for (let rows = 1; rows <= Math.min(fitRows, shown); rows++) {
        const columns = Math.ceil(shown / rows);

        if (columns > fitColumns) {
            continue;
        }

        // How large a slot of the best shape can be in its cell.
        const scale = Math.min(width / columns / SLOT_ASPECT, height / rows);

        if (scale > best.scale + 0.5) {
            best = { rows, columns, scale };
        }
    }

    return { rows: best.rows, columns: best.columns, shown };
}

/** Where everything goes in a tile of `width` × `height` (WT-3, WT-7): the strip, then the slots in rows. */
export function forecastLayout(content: ForecastContent, width: number, height: number) : ForecastLayout {
    const stripHeight = content.warnings.length > 0 ? Math.min(34, Math.max(22, height * 0.1)) : 0;
    const strip = stripHeight > 0 ? { left: 0, top: 0, width, height: stripHeight } : undefined;
    const top = stripHeight > 0 ? stripHeight + 4 : 0;
    const area = { width, height: height - top };

    if (content.slots.length === 0 || area.height <= 0) {
        return { strip, slots: [] };
    }

    const grid = slotGrid(content.slots.length, area.width, area.height);
    const cellWidth = area.width / grid.columns;
    const cellHeight = area.height / grid.rows;
    const slots: Rect[] = [];

    for (let index = 0; index < grid.shown; index++) {
        const row = Math.floor(index / grid.columns);
        const column = index % grid.columns;

        slots.push({ left: column * cellWidth, top: top + row * cellHeight, width: cellWidth, height: cellHeight });
    }

    return { strip, slots };
}

// ---- Drawing ----------------------------------------------------------------------------------------

export type IconLookup = (name: string) => IconDefinition | undefined;

const ink = (font: TileFont, options: Partial<TextStyle> = {}) : TextStyle => ({ family: font.family, metrics: font.metrics, ...options });

/** A small arrow in `box`, pointing to `toward` degrees (0 up, clockwise). */
function drawArrow(ctx: Context, box: Rect, toward: number, color: string) : void {
    const size = Math.min(box.width, box.height);

    ctx.save();
    ctx.translate(box.left + box.width / 2, box.top + box.height / 2);
    ctx.rotate((toward * Math.PI) / 180);
    ctx.beginPath();
    ctx.moveTo(0, -size * 0.45);
    ctx.lineTo(size * 0.3, size * 0.35);
    ctx.lineTo(0, size * 0.18);
    ctx.lineTo(-size * 0.3, size * 0.35);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
    ctx.restore();
}

/** A warning's mark: a small amber triangle. */
function drawWarningMark(ctx: Context, x: number, y: number, size: number) : void {
    ctx.beginPath();
    ctx.moveTo(x, y + size);
    ctx.lineTo(x + size / 2, y);
    ctx.lineTo(x + size, y + size);
    ctx.closePath();
    ctx.fillStyle = WARNING_COLOR;
    ctx.fill();
}

type SlotPart = 'when' | 'icon' | 'temperature' | 'rain' | 'wind';

/**
 * A slot's lines (WT-4), each its share of the slot's height, top to bottom: the weather (icon and
 * temperature) large, when it is and the details (rain, wind) smaller, after a gap (the owner,
 * 2026-10-28: "a bit crowded"). A slot lower than its least height leaves out rain and wind, so that
 * what it keeps is readable (WT-7; the Mac's one-horizon results, note 1: every line was a few pixels
 * high in a wide, low tile).
 */
function slotRows(slot: ForecastSlot, box: Rect) : { part: SlotPart; row: Rect }[] {
    const pad = Math.min(box.width, box.height) * 0.06;
    const inner = { left: box.left + pad, top: box.top + pad, width: box.width - 2 * pad, height: box.height - 2 * pad };
    const isLow = box.height < MIN_SLOT_HEIGHT;
    const lines: { part: SlotPart | 'gap'; weight: number }[] = [{ part: 'when', weight: 0.13 }, { part: 'icon', weight: 0.32 }, { part: 'temperature', weight: 0.22 }];
    const details: { part: SlotPart; weight: number }[] = [];

    if (slot.rain !== undefined && !isLow) {
        details.push({ part: 'rain', weight: 0.12 });
    }
    if (slot.wind !== undefined && !isLow) {
        details.push({ part: 'wind', weight: 0.12 });
    }
    if (details.length > 0) {
        lines.push({ part: 'gap', weight: 0.04 }, ...details);
    }

    const weights = lines.reduce((sum, line) => sum + line.weight, 0);
    let y = inner.top;
    const rows: { part: SlotPart; row: Rect }[] = [];

    for (const line of lines) {
        const height = (inner.height * line.weight) / weights;

        if (line.part !== 'gap') {
            rows.push({ part: line.part, row: { left: inner.left, top: y, width: inner.width, height } });
        }

        y += height;
    }

    return rows;
}

/** A text's height in its line, of the line's: some air between the lines (the owner, 2026-10-28). */
const TEXT_HEIGHT = 0.72;
/** The temperature fills more of its line: it is what the slot is about. */
const TEMPERATURE_HEIGHT = 0.78;
/** The faint line between slots (the owner, 2026-10-28), so that each column reads as one. */
const DIVIDER_COLOR = 'rgba(255, 255, 255, 0.08)';

const rainText = (slot: ForecastSlot) : string => (slot.chance ? `${slot.rain} · ${slot.chance}` : (slot.rain ?? ''));

/**
 * Whether a slot's rain line is drawn: not where no rain is expected ("0 mm"), unless a threshold
 * colours it (the owner, 2026-10-28). The line keeps its place, so that the wind lines stay level.
 */
const showsRain = (slot: ForecastSlot) : boolean => slot.rain !== undefined && (!slot.isDry || slot.rainColor !== undefined);

/** How far a slot's rain line must shrink to fit its width: 1 where it fits as it is. */
function rainFit(ctx: Context, slot: ForecastSlot, box: Rect, font: TileFont) : number {
    const row = slotRows(slot, box).find((line) => line.part === 'rain')?.row;
    if (!row || !showsRain(slot)) {
        return 1;
    }
    const width = layoutText(ctx, rainText(slot), Number.MAX_SAFE_INTEGER, row.height * TEXT_HEIGHT, ink(font)).bounds.width;

    return Math.min(1, row.width / Math.max(1, width));
}

/** How far a slot's wind line must shrink to fit its width: 1 where it fits as it is. */
function windFit(ctx: Context, slot: ForecastSlot, box: Rect, font: TileFont, windUnit: string) : number {
    const row = slotRows(slot, box).find((line) => line.part === 'wind')?.row;

    return row ? Math.min(1, row.width / Math.max(1, windTotal(windWidths(ctx, row, slot.wind ?? '', windUnit, slot.windToward, font, 1)))) : 1;
}

/** The size of a warning's mark in a slot. */
const markSizeOf = (box: Rect) : number => Math.max(7, Math.min(box.width, box.height) * 0.1);

/**
 * The side of a slot's icon, or of each of its two (the weather turning), as large as its row
 * allows: two side by side are limited by the width, one by the height. Clear of the warning's mark,
 * where there is one (the Mac's wind and colours results, note 1).
 */
function iconSideOf(slot: ForecastSlot, row: Rect, markSize: number) : number {
    const clearHalf = slot.isWarned ? row.width / 2 - markSize * 1.2 : row.width / 2;

    return slot.conditions.length > 1
        ? Math.min(row.height * 0.78, row.width * 0.4, clearHalf / 1.12)
        // Some air above the temperature (the owner, 2026-10-28: "crowded").
        : Math.min(row.height * 0.82, row.width * 0.8, clearHalf * 2);
}

/** The side of a slot's icon as large as it can be, for the tile's one size. */
function iconFit(slot: ForecastSlot, box: Rect) : number {
    const row = slotRows(slot, box).find((line) => line.part === 'icon')?.row;

    return row ? iconSideOf(slot, row, markSizeOf(box)) : Number.POSITIVE_INFINITY;
}

/**
 * One slot (WT-4): when, the icon or icons, the temperature, rain, wind; a mark when warned.
 * `rainScale` and `windScale`: the rain and wind lines' sizes, each the same for every slot of the
 * tile (the Mac's wind and colours results, note 2, and its mark and rain results, note 2: each slot
 * fitted its own before). `iconSide`: the icons' size, the same for every slot of the tile.
 */
export function drawSlot(ctx: Context, slot: ForecastSlot, box: Rect, icons: IconLookup, font: TileFont, color: string, windUnit: string, rainScale = 1, windScale?: number, iconSide?: number) : void {
    const markSize = markSizeOf(box);

    for (const { part, row } of slotRows(slot, box)) {
        const text = (value: string, shade: string, box2: Rect = row, align: 'left' | 'center' | 'right' = 'center') : void => {
            drawText(ctx, value, { left: box2.left, top: box2.top + box2.height * 0.08, width: box2.width, height: box2.height * TEXT_HEIGHT }, shade, ink(font, { align, valign: 'center' }));
        };

        switch (part) {
            case 'when':
                text(slot.when, DIM_COLOR);
                break;

            case 'icon': {
                // The warning's mark at the icon row's corner, clear of the label above.
                if (slot.isWarned) {
                    drawWarningMark(ctx, row.left + row.width - markSize, row.top, markSize);
                }
                const conditions = slot.conditions;
                const side = Math.min(iconSide ?? Number.POSITIVE_INFINITY, iconSideOf(slot, row, markSize));
                if (conditions.length > 1) {
                    // Two icons with an arrow between: the weather turning.
                    const middle = row.left + row.width / 2;
                    const iconTop = row.top + (row.height - side) / 2;
                    const first = icons(conditions[0]!.icon);
                    const second = icons(conditions[1]!.icon);
                    if (first) {
                        drawIcon(ctx, first, { left: middle - side * 1.12, top: iconTop, width: side, height: side }, iconColor(conditions[0]!));
                    }
                    if (second) {
                        drawIcon(ctx, second, { left: middle + side * 0.12, top: iconTop, width: side, height: side }, iconColor(conditions[1]!));
                    }
                    drawArrow(ctx, { left: middle - side * 0.12, top: iconTop + side * 0.38, width: side * 0.24, height: side * 0.24 }, 90, DIM_COLOR);
                } else if (conditions[0]) {
                    const icon = icons(conditions[0].icon);
                    if (icon) {
                        drawIcon(ctx, icon, { left: row.left + (row.width - side) / 2, top: row.top + (row.height - side) / 2, width: side, height: side }, iconColor(conditions[0]));
                    }
                }
                break;
            }

            case 'temperature': {
                const height = row.height * TEMPERATURE_HEIGHT;
                drawText(ctx, slot.temperature, { left: row.left, top: row.top + (row.height - height) / 2, width: row.width, height }, color, ink(font, { align: 'center', valign: 'center' }));
                break;
            }

            case 'rain': {
                if (!showsRain(slot)) {
                    break;
                }
                const shade = slot.rainColor ?? (slot.isDry ? DIM_COLOR : '#8cc8ff');
                const height = row.height * TEXT_HEIGHT * rainScale;
                drawText(ctx, rainText(slot), { left: row.left, top: row.top + (row.height - height) / 2, width: row.width, height }, shade, ink(font, { align: 'center', valign: 'center' }));
                break;
            }

            case 'wind':
                drawWind(ctx, row, slot.wind ?? '', windUnit, slot.windToward, slot.windColor ?? DIM_COLOR, font, windScale);
                break;
        }
    }
}

/** How large the wind's unit is beside its number (the owner, #20: "should be smaller"). */
const WIND_UNIT_SCALE = 0.62;

interface WindWidths {
    readonly number: number;
    readonly unit: number;
    readonly gap: number;
    readonly arrow: number;
}

/** The widths of a wind line's parts at `scale` of its row's height. */
function windWidths(ctx: Context, row: Rect, wind: string, unit: string, toward: number | undefined, font: TileFont, scale: number) : WindWidths {
    const style = ink(font, { align: 'left', valign: 'top' });
    const height = row.height * TEXT_HEIGHT;

    return {
        number: layoutText(ctx, wind, Number.MAX_SAFE_INTEGER, height * scale, style).bounds.width,
        unit: layoutText(ctx, unit, Number.MAX_SAFE_INTEGER, height * WIND_UNIT_SCALE * scale, style).bounds.width,
        gap: height * 0.14 * scale,
        arrow: toward === undefined ? 0 : row.height * 0.62 * scale,
    };
}

const windTotal = (parts: WindWidths) : number => parts.number + parts.gap + parts.unit + (parts.arrow > 0 ? parts.gap + parts.arrow : 0);

/**
 * The wind's line (WT-4): the number, its unit smaller on the same baseline, and the arrow, centred
 * together in `row`; all smaller where they do not fit its width, or at the tile's `common` size.
 */
function drawWind(ctx: Context, row: Rect, wind: string, unit: string, toward: number | undefined, color: string, font: TileFont, common?: number) : void {
    const style = ink(font, { align: 'left', valign: 'top' });
    const height = row.height * TEXT_HEIGHT;
    const scale = common ?? Math.min(1, row.width / Math.max(1, windTotal(windWidths(ctx, row, wind, unit, toward, font, 1))));
    const parts = windWidths(ctx, row, wind, unit, toward, font, scale);
    const numberBox = { left: row.left + (row.width - windTotal(parts)) / 2, top: row.top + (row.height - height * scale) / 2, width: parts.number + 1, height: height * scale };
    const unitHeight = height * WIND_UNIT_SCALE * scale;
    // The unit's baseline on the number's.
    const unitTop = numberBox.top + layoutText(ctx, wind, numberBox.width, numberBox.height, style).baseline - layoutText(ctx, unit, parts.unit + 1, unitHeight, style).baseline;
    drawText(ctx, wind, numberBox, color, style);
    const unitLeft = numberBox.left + parts.number + parts.gap;
    drawText(ctx, unit, { left: unitLeft, top: unitTop, width: parts.unit + 1, height: unitHeight }, color, style);

    if (toward !== undefined) {
        const side = row.height * 0.62 * scale;
        drawArrow(ctx, { left: unitLeft + parts.unit + parts.gap, top: row.top + (row.height - side) / 2, width: side, height: side }, toward, color);
    }
}

/** The warning strip (WT-5): amber, the first warning's icon and line. */
export function drawWarningStrip(ctx: Context, box: Rect, warnings: readonly WeatherWarning[], icons: IconLookup, font: TileFont, now: number, timeZone: string) : void {
    ctx.beginPath();
    ctx.roundRect(box.left, box.top, box.width, box.height, Math.min(6, box.height / 3));
    ctx.fillStyle = WARNING_COLOR;
    ctx.fill();

    const side = box.height * 0.62;
    const icon = warnings[0] ? icons(warnings[0].icon) : undefined;

    if (icon) {
        drawIcon(ctx, icon, { left: box.left + box.height * 0.3, top: box.top + (box.height - side) / 2, width: side, height: side }, WARNING_TEXT);
    }

    const left = box.left + box.height * 0.3 + side + box.height * 0.3;

    drawText(ctx, warningLine(warnings, now, timeZone), { left, top: box.top + box.height * 0.2, width: box.left + box.width - left - box.height * 0.3, height: box.height * 0.6 }, WARNING_TEXT, ink(font, { align: 'left', valign: 'center' }));
}

/** The whole tile, laid out for its size. */
export function drawForecast(ctx: Context, content: ForecastContent, layout: ForecastLayout, icons: IconLookup, font: TileFont, color: string, now: number) : void {
    if (layout.strip) {
        drawWarningStrip(ctx, layout.strip, content.warnings, icons, font, now, content.timeZone);
    }

    const shown = layout.slots.flatMap((box, index) => (content.slots[index] ? [{ slot: content.slots[index], box }] : []));
    // One size for the rain lines: the one that fits the longest.
    const rainScale = Math.min(1, ...shown.map(({ slot, box }) => rainFit(ctx, slot, box, font)));
    // And for the wind lines.
    const windScale = Math.min(1, ...shown.map(({ slot, box }) => windFit(ctx, slot, box, font, content.windUnit)));
    // A faint line left of every slot but a row's first.
    const rowStart = Math.min(...shown.map(({ box }) => box.left));
    
    ctx.strokeStyle = DIVIDER_COLOR;
    ctx.lineWidth = 1;

    for (const { box } of shown) {
        if (box.left > rowStart) {
            const x = Math.round(box.left) + 0.5;
            ctx.beginPath();
            ctx.moveTo(x, box.top + box.height * 0.12);
            ctx.lineTo(x, box.top + box.height * 0.88);
            ctx.stroke();
        }
    }

    // And for the icons: one alone as large as one of a pair (the owner, 2026-10-28: "much bigger when
    // there's only one icon").
    const iconSide = Math.min(...shown.map(({ slot, box }) => iconFit(slot, box)));
    
    for (const { slot, box } of shown) {
        drawSlot(ctx, slot, box, icons, font, color, content.windUnit, rainScale, windScale, Number.isFinite(iconSide) ? iconSide : undefined);
    }
}

/** Every icon name the tile draws, to load them before drawing. */
export function iconNamesOf(content: ForecastContent) : string[] {
    return [...new Set([...content.slots.flatMap((slot) => slot.conditions.map((condition) => condition.icon)), ...content.warnings.map((warning) => warning.icon)])].sort();
}
